
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModernNotification } from '../hooks/useModernNotification';
import {
  getProfileMediaFitMap,
  getConfigForMedia,
  getProfileMediaStyles,
  getBlurBgStyles,
  DEFAULT_PROFILE_FIT_MAP,
  type ProfileMediaFitMap,
} from '../services/profileMediaFitSettings';

interface ProfileMediaProps {
  profileMedia: string[];
  mediaIsVideo: boolean[];
  currentMediaIndex: number;
  setCurrentMediaIndex: (index: number) => void;
}

const ProfileMedia: React.FC<ProfileMediaProps> = React.memo(
  ({ profileMedia, mediaIsVideo, currentMediaIndex, setCurrentMediaIndex }) => {
    const { t } = useTranslation();
    const notification = useModernNotification();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // v5: map des paramètres d'adaptation par média
    const [fitMap, setFitMap] = useState<ProfileMediaFitMap>(DEFAULT_PROFILE_FIT_MAP);
    useEffect(() => {
      let mounted = true;
      getProfileMediaFitMap().then((m) => {
        if (mounted) setFitMap(m);
      });
      const onUpdate = (e: Event) => {
        const ce = e as CustomEvent<ProfileMediaFitMap>;
        if (ce.detail) setFitMap(ce.detail);
      };
      window.addEventListener('profileMediaFitUpdated', onUpdate);
      return () => {
        mounted = false;
        window.removeEventListener('profileMediaFitUpdated', onUpdate);
      };
    }, []);

    useEffect(() => {
      if (!profileMedia || profileMedia.length === 0) return;

      if (profileMedia.length === 1 && mediaIsVideo[0]) {
        if (videoRef.current) {
          videoRef.current.loop = true;
          videoRef.current.play().catch((err) => {
            console.error(t('Erreur lecture vidéo profil'), err);
            notification.showNotification({
              title: t('error'),
              message: t('error_load_video'),
              type: 'error',
            });
          });
        }
        return;
      }

      let interval: NodeJS.Timeout | null = null;
      let videoHandler: (() => void) | null = null;

      const advanceToNextMedia = () => {
        setCurrentMediaIndex(
          (prev: number) => (prev + 1) % profileMedia.length
        );
      };

      const setupMedia = () => {
        if (videoRef.current && videoHandler) {
          videoRef.current.removeEventListener('ended', videoHandler);
          videoHandler = null;
        }

        if (interval) {
          clearInterval(interval);
          interval = null;
        }

        if (mediaIsVideo[currentMediaIndex]) {
          if (videoRef.current) {
            videoHandler = () => advanceToNextMedia();
            videoRef.current.addEventListener('ended', videoHandler);
            videoRef.current.play().catch((err) => {
              console.error(t('Erreur lecture vidéo profil'), err);
              advanceToNextMedia();
            });
          }
        } else {
          interval = setInterval(advanceToNextMedia, 5000);
        }
      };

      setupMedia();

      return () => {
        if (interval) clearInterval(interval);
        if (videoRef.current && videoHandler) {
          videoRef.current.removeEventListener('ended', videoHandler);
        }
      };
    }, [
      profileMedia,
      mediaIsVideo,
      currentMediaIndex,
      setCurrentMediaIndex,
      t,
      notification,
    ]);

    if (!profileMedia || profileMedia.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-t-2xl">
          <span className="sr-only">{t('Aucun média disponible')}</span>
          <svg
            fill="#f97316"
            height="40px"
            width="40px"
            viewBox="0 0 60 60"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <title>{t('Placeholder média profil')}</title>
            {/* SVG Placeholder identique à App.tsx */}
            <path d="M18.35,20.805c0.195,0.195,0.451,0.293,0.707,0.293c0.256,0,0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414c-1.015-1.016-1.015-2.668,0-3.684c0.87-0.87,1.35-2.026,1.35-3.256s-0.479-2.386-1.35-3.256c-0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414c0.492,0.492,0.764,1.146,0.764,1.842s-0.271,1.35-0.764,1.842C16.555,16.088,16.555,19.01,18.35,20.805z" />
            <path d="M40.35,20.805c0.195,0.195,0.451,0.293,0.707,0.293c0.256,0,0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414c-1.015-1.016-1.015-2.668,0-3.684c0.87-0.87,1.35-2.026,1.35-3.256s-0.479-2.386-1.35-3.256c0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414c0.492,0.492,0.764,1.146,0.764,1.842s-0.271,1.35-0.764,1.842C38.555,16.088,38.555,19.01,40.35,20.805z" />
            <path d="M29.35,14.805c0.195,0.195,0.451,0.293,0.707,0.293c0.256,0,0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414c-1.015-1.016-1.015-2.668,0-3.684c0.87-0.87,1.35-2.026,1.35-3.256s-0.479-2.386-1.35-3.256c-0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414c0.492,0.492,0.764,1.146,0.764,1.842s-0.271,1.35-0.764,1.842C27.555,10.088,27.555,13.01,29.35,14.805z" />
            <path d="M55.624,43.721C53.812,33.08,45.517,24.625,34.957,22.577c0.017-0.16,0.043-0.321,0.043-0.48c0-2.757-2.243-5-5-5s-5,2.243-5,5c0,0.159,0.025,0.32,0.043,0.48C14.483,24.625,6.188,33.08,4.376,43.721C2.286,44.904,0,46.645,0,48.598c0,5.085,15.512,8.5,30,8.5s30-3.415,30-8.5C60,46.645,57.714,44.904,55.624,43.721z M27.006,22.27C27.002,22.212,27,22.154,27,22.098c0-1.654,1.346-3,3-3s3,1.346,3,3c0,0.057-0.002,0.114-0.006,0.172c-0.047-0.005-0.094-0.007-0.140-0.012c-0.344-0.038-0.690-0.065-1.038-0.089c-0.128-0.009-0.255-0.022-0.383-0.029c-0.474-0.026-0.951-0.041-1.432-0.041s-0.958,0.015-1.432,0.041c-0.128,0.007-0.255,0.020-0.383,0.029c-0.348,0.024-0.694,0.052-1.038,0.089C27.100,22.263,27.053,22.264,27.006,22.27z M25.126,26.635c1.582-0.356,3.217-0.537,4.860-0.537c0.004,0,0.009,0,0.014,0c0.552,0,1,0.448,1,1.001c0,0.552-0.448,0.999-1,0.999h0c-0.004,0-0.009,0-0.013,0c-1.496,0-2.982,0.164-4.421,0.488c-0.074,0.017-0.148,0.024-0.221,0.024c-0.457,0-0.870-0.315-0.975-0.780C24.249,27.291,24.587,26.756,25.126,26.635z M19.150,28.997c0.476-0.281,1.088-0.124,1.370,0.351c0.282,0.476,0.125,1.089-0.351,1.370c-4.713,2.792-8.147,7.861-9.186,13.560c-0.088,0.482-0.509,0.820-0.983,0.820c-0.060,0-0.120-0.005-0.180-0.017c-0.543-0.099-0.904-0.619-0.805-1.163C10.158,37.658,13.947,32.080,19.150,28.997z M30,55.098c-17.096,0-28-4.269-28-6.500c0-0.383,0.474-1.227,2.064-2.328c-0.004,0.057-0.002,0.113-0.006,0.170C4.024,46.988,4,47.540,4,48.098v0.788l0.767,0.185c8.254,1.981,16.744,2.985,25.233,2.985s16.979-1.004,25.233-2.985L56,48.886v-0.788c0-0.558-0.024-1.109-0.058-1.658c-0.004-0.057-0.002-0.113-0.006-1.658c-0.004-0.057-0.002-0.113-0.006-0.170C57.526,47.371,58,48.215,58,48.598C58,50.829,47.096,55.098,30,55.098z" />
          </svg>
        </div>
      );
    }

    const currentSrc = profileMedia[currentMediaIndex];
    const isVid = mediaIsVideo[currentMediaIndex];
    // v5: récupère le config spécifique au média courant (fallback sur default)
    const fitForCurrent = getConfigForMedia(fitMap, currentSrc);
    const mediaStyles = getProfileMediaStyles(fitForCurrent);
    const blurStyles = getBlurBgStyles(fitForCurrent);
    const showBlurBg = fitForCurrent.fitMode === 'blur-bg';

    return (
      <div className="relative w-full h-full rounded-b-2xl shadow-lg overflow-hidden">
        {/* v5: fond flou (mode blur-bg uniquement) */}
        {showBlurBg && currentSrc && (
          isVid ? (
            <video
              key={`bg-${currentSrc}`}
              src={currentSrc}
              style={blurStyles}
              autoPlay
              muted
              playsInline
              loop
              aria-hidden="true"
            />
          ) : (
            <img
              key={`bg-${currentSrc}`}
              src={currentSrc}
              alt=""
              style={blurStyles}
              aria-hidden="true"
            />
          )
        )}

        {/* Média principal */}
        {isVid ? (
          <video
            key={currentSrc}
            ref={videoRef}
            src={currentSrc}
            style={mediaStyles}
            autoPlay
            muted
            playsInline
            loop={profileMedia.length === 1}
            aria-hidden="true"
            onError={() => {
              console.error(
                t('Erreur chargement vidéo'),
                profileMedia[currentMediaIndex]
              );
              notification.showNotification({
                title: t('error'),
                message: t('error_load_video'),
                type: 'error',
              });
              setCurrentMediaIndex(
                (prev: number) => (prev + 1) % profileMedia.length
              );
            }}
          />
        ) : (
          <img
            key={currentSrc}
            src={currentSrc}
            alt={t('Logo restaurant')}
            style={mediaStyles}
            onError={() => {
              console.error(
                t('Erreur chargement image'),
                profileMedia[currentMediaIndex]
              );
              notification.showNotification({
                title: t('error'),
                message: t('error_load_image'),
                type: 'error',
              });
              setCurrentMediaIndex(
                (prev: number) => (prev + 1) % profileMedia.length
              );
            }}
          />
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/6 bg-gradient-to-t from-orange-100 to-transparent pointer-events-none z-[2]" />
        <div className="absolute inset-0 bg-black/20 pointer-events-none z-[2]" />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.profileMedia === nextProps.profileMedia &&
      prevProps.mediaIsVideo === nextProps.mediaIsVideo &&
      prevProps.currentMediaIndex === nextProps.currentMediaIndex
    );
  }
);

export default ProfileMedia;
